/* Nota Tanıma — ders içerikleri. Blok türleri ve satır içi işaretler: ./blocks.ts */
import { cell, compare, go, h, type Lesson, list, p, play, table, tip } from './blocks';

export const nota: Lesson[] = [
  {
    id: 'n1',
    title: 'Müzik alfabesi: Do Re Mi ve C D E',
    minutes: 4,
    summary: `Türkiye'de kullanılan iki nota adlandırma sistemini, solfej ve harfleri, birbirine bağla.`,
    blocks: [
      p(
        `Batı müziğinde 7 temel nota vardır. Türkiye'de müzik derslerinde genelde **solfej** adları (Do, Re, Mi…) kullanılır; akor adlarında ve dünyanın çoğunda ise **harfler** (C, D, E…). İkisi de aynı notaları anlatır.`,
      ),
      table(
        ['Solfej', 'Harf', 'Dinle'],
        [
          ['Do', 'C', cell({ seq: 'C4' })],
          ['Re', 'D', cell({ seq: 'D4' })],
          ['Mi', 'E', cell({ seq: 'E4' })],
          ['Fa', 'F', cell({ seq: 'F4' })],
          ['Sol', 'G', cell({ seq: 'G4' })],
          ['La', 'A', cell({ seq: 'A4' })],
          ['Si', 'B', cell({ seq: 'B4' })],
        ],
      ),
      tip(
        'info',
        'Akorlarda harf, notada solfej',
        `Gitarcılar arasında "La minör" yerine çoğu zaman "A minör" ya da kısaca {{c:Am}} denir. İki adlandırmayı da tanımak işini kolaylaştırır.`,
      ),
      h(`Oktav: aynı adın yeniden gelmesi`),
      p(
        `Yedinci notadan sonra ilk nota yeniden gelir, ama bu kez daha incedir. Aynı adı taşıyan ve biri diğerinin tam iki katı frekansta olan iki notaya **oktav** denir. Bu yüzden notalara bazen bir numara eklenir: {{n:C4}} ile {{n:C5}} aynı notanın iki farklı oktavıdır.`,
      ),
      play('Majör gam: bir oktav yukarı', { seq: 'C4 D4 E4 F4 G4 A4 B4 C5:2', bpm: 100 }),
      compare([
        ['{{n:C4}}', { seq: 'C4' }],
        ['Bir oktav yukarı: {{n:C5}}', { seq: 'C5' }],
        ['İkisi birlikte', { harm: 'C4 C5' }],
      ]),
      p(
        `Bu uygulamada notaların nasıl yazılacağını **Ayarlar**'dan seçebilirsin: yalnızca solfej, yalnızca harf ya da ikisi birlikte.`,
      ),
      {
        t: 'quiz',
        q: `G harfi hangi notadır?`,
        options: ['Fa', 'Sol', 'La'],
        answer: 1,
        explain: `G = Sol. Sırayı hatırlamanın kolay yolu: C D E F G A B = Do Re Mi Fa Sol La Si.`,
      },
      {
        t: 'quiz',
        q: `Hangi akor "La minör" demektir?`,
        options: ['Am', 'Em', 'Dm'],
        answer: 0,
        explain: `A = La; küçük "m" harfi minör demektir: {{c:Am}}.`,
      },
      go('Sap haritasını keşfet', 'nota.kesfet'),
    ],
  },
  {
    id: 'n2',
    title: 'Diyez ve bemol',
    minutes: 5,
    summary: `12 notanın tamamını, diyez ve bemolün ne anlama geldiğini ve hangi notalar arasında ara nota olmadığını öğren.`,
    blocks: [
      p(
        `7 temel notanın arasında 5 ara nota daha vardır; toplam 12 nota eder. Piyanoda bunlar siyah tuşlardır. Gitarda ise her perde bir sonraki notadır: 12 perde ilerlediğinde aynı notanın bir oktav yukarısına varırsın.`,
      ),
      list([
        `**Diyez (♯)** notayı yarım ses yükseltir: {{pc:F#}}, {{pc:F}} notasının yarım ses yukarısıdır.`,
        `**Bemol (♭)** notayı yarım ses alçaltır: {{pc:Bb}}, {{pc:B}} notasının yarım ses aşağısıdır.`,
        `Aynı ses iki farklı adla yazılabilir: {{pc:C#}} ile {{pc:Db}} aynı perdedir. Buna **enharmonik** denir.`,
      ]),
      {
        t: 'keyboard',
        from: 'C4',
        to: 'C5',
        highlight: 'C#4 D#4 F#4 G#4 A#4',
        caption: `Siyah tuşlar ara notalardır. {{pc:E}}–{{pc:F}} ve {{pc:B}}–{{pc:C}} arasında siyah tuş yoktur.`,
      },
      h(`Ara notası olmayan iki yer`),
      p(
        `{{pc:E}} ile {{pc:F}} arasında ve {{pc:B}} ile {{pc:C}} arasında ara nota yoktur; bu notalar zaten yarım ses uzaklıktadır. Gitarda da bu yüzden {{pc:E}} notasından bir perde ilerlediğinde doğrudan {{pc:F}} gelir.`,
      ),
      compare([
        ['{{pc:E}} → {{pc:F}} (yarım ses)', { seq: 'E4 F4' }],
        ['{{pc:F}} → {{pc:G}} (tam ses)', { seq: 'F4 G4' }],
      ]),
      play('12 notanın hepsi (kromatik gam)', { seq: 'C4 C#4 D4 D#4 E4 F4 F#4 G4 G#4 A4 A#4 B4 C5:2', bpm: 120 }),
      table(
        ['Diyezle', 'Bemolle', 'Gitarda bir örnek'],
        [
          ['{{pc:C#}}', '{{pc:Db}}', '5. tel, 4. perde'],
          ['{{pc:D#}}', '{{pc:Eb}}', '5. tel, 6. perde'],
          ['{{pc:F#}}', '{{pc:Gb}}', '6. tel, 2. perde'],
          ['{{pc:G#}}', '{{pc:Ab}}', '6. tel, 4. perde'],
          ['{{pc:A#}}', '{{pc:Bb}}', '5. tel, 1. perde'],
        ],
      ),
      {
        t: 'quiz',
        q: `{{pc:A#}} ile aynı ses hangisidir?`,
        options: ['{{pc:Bb}}', '{{pc:Ab}}', '{{pc:B}}'],
        answer: 0,
        explain: `{{pc:A}} notasının yarım ses yukarısı, {{pc:B}} notasının yarım ses aşağısıdır: {{pc:A#}} = {{pc:Bb}}.`,
      },
      {
        t: 'quiz',
        q: `Hangi iki nota arasında ara nota yoktur?`,
        options: ['{{pc:C}} ile {{pc:D}}', '{{pc:E}} ile {{pc:F}}', '{{pc:G}} ile {{pc:A}}'],
        answer: 1,
        explain: `{{pc:E}}–{{pc:F}} ve {{pc:B}}–{{pc:C}} arası zaten yarım sestir; aralarına başka nota sığmaz.`,
      },
      go('Sap haritasında 12 notayı gör', 'nota.kesfet'),
      go('Gitarda olmayan sesler: koma', 'az-bilinenler.koma-sesler'),
    ],
  },
  {
    id: 'n3',
    title: 'Gitarın boş telleri',
    minutes: 4,
    summary: `Altı boş telin adını ve sesini öğren; sapta her şey bu altı notadan başlar.`,
    blocks: [
      p(
        `Standart akortta gitarın altı teli aşağıdaki notalara akort edilir. Teller inceden kalına doğru numaralanır: **1. tel en ince**, **6. tel en kalın** teldir.`,
      ),
      table(
        ['Tel', 'Nota', 'Dinle'],
        [
          ['6. tel (en kalın)', '{{n:E2}}', cell({ seq: 'E2' })],
          ['5. tel', '{{n:A2}}', cell({ seq: 'A2' })],
          ['4. tel', '{{n:D3}}', cell({ seq: 'D3' })],
          ['3. tel', '{{n:G3}}', cell({ seq: 'G3' })],
          ['2. tel', '{{n:B3}}', cell({ seq: 'B3' })],
          ['1. tel (en ince)', '{{n:E4}}', cell({ seq: 'E4' })],
        ],
      ),
      h(`Akılda tutmak için`),
      p(
        `Harflerle E – A – D – G – B – E sırasını şu cümleyle hatırlayabilirsin: **E**lif **A**blası'yla **D**ün **G**ece **B**ahçede **E**ğlendi. Solfejle söylersek: Mi, La, Re, Sol, Si, Mi.`,
      ),
      play('Altı boş tel, kalından inceye', { seq: 'E2 A2 D3 G3 B3 E4:2', bpm: 80 }),
      h(`5. perde kuralı`),
      p(
        `Bir teli 5. perdeden bastığında bir sonraki (daha ince) telin boş sesini elde edersin. Tek istisna 3. teldir: 2. telin boş sesi ({{n:B3}}) 3. telin 4. perdesindedir. Bu kural hem akort etmek hem de sapta aynı notayı bulmak için işe yarar.`,
      ),
      {
        t: 'fretboard',
        notes: 'E2 A2 D3 G3 B3 E4',
        from: 0,
        to: 5,
        caption: `Boş tellerin sesleri sapta bir kez daha çıkar: bir telin 5. perdesi (3. telde 4. perde), bir sonraki boş telle aynı sestir.`,
      },
      compare([
        ['6. tel, 5. perde', { seq: 'A2' }],
        ['5. tel, boş', { seq: 'A2' }],
      ]),
      {
        t: 'quiz',
        q: `Gizli notayı dinle, sonra üç boş telle karşılaştır. Gizli nota hangi tel?`,
        items: [
          { label: 'Gizli nota', seq: 'D3' },
          { label: '6. tel', seq: 'E2' },
          { label: '5. tel', seq: 'A2' },
          { label: '4. tel', seq: 'D3' },
        ],
        options: ['6. tel', '5. tel', '4. tel'],
        answer: 2,
        explain: `Gizli nota {{n:D3}}, yani 4. telin boş sesi.`,
      },
      go('Akort aletini aç', 'araclar.akort'),
      go('Teller neden 5 yarım ses arayla?', 'az-bilinenler.tel-araliklari'),
    ],
  },
  {
    id: 'n4',
    title: 'Sapta notaları bulmak',
    minutes: 7,
    summary: `Her perdenin yarım ses olduğunu kullanarak sapı ezberlemeden okumayı ve 6. ile 5. teldeki dönüm noktalarını öğren.`,
    blocks: [
      p(`Sapı ezberlemek için 72 ayrı yeri tek tek öğrenmen gerekmez. İki kural ve birkaç dönüm noktası yeter.`),
      list([
        `**Her perde yarım sestir.** Boş telin adını biliyorsan, perde sayarak her notayı bulursun.`,
        `**12. perde oktavdır.** 12. perde boş telle aynı adı taşır ve sap oradan sonra kendini tekrar eder.`,
        `**Perde işaretleri** 3., 5., 7., 9. ve 12. perdededir (12. perdede çift nokta). Saymaya bu işaretlerden başla.`,
      ]),
      h(`6. teldeki doğal notalar`),
      table(
        ['Perde', '0', '1', '3', '5', '7', '8', '10', '12'],
        [['Nota', '{{pc:E}}', '{{pc:F}}', '{{pc:G}}', '{{pc:A}}', '{{pc:B}}', '{{pc:C}}', '{{pc:D}}', '{{pc:E}}']],
      ),
      h(`5. teldeki doğal notalar`),
      table(
        ['Perde', '0', '2', '3', '5', '7', '8', '10', '12'],
        [['Nota', '{{pc:A}}', '{{pc:B}}', '{{pc:C}}', '{{pc:D}}', '{{pc:E}}', '{{pc:F}}', '{{pc:G}}', '{{pc:A}}']],
      ),
      tip(
        'success',
        'Neden önce bu iki tel?',
        `Barre akorların kök notası 6. ya da 5. teldedir. Bu iki teli bildiğinde {{c:F}}, {{c:Bm}} ya da {{c:C#m}} gibi akorlara nereden basacağını hemen bulursun.`,
      ),
      {
        t: 'fretboard',
        pcs: 'C D E F G A B',
        root: 'C',
        from: 0,
        to: 12,
        caption: `Bütün tellerde doğal notalar, 0–12. perdeler. Vurgulu olanlar {{pc:C}}.`,
      },
      h(`Oktav şekilleri`),
      list([
        `6. teldeki bir notanın oktavı **iki tel ince, iki perde ileri**dedir (4. tel). Örnek: 6. tel 3. perde {{n:G2}} → 4. tel 5. perde {{n:G3}}.`,
        `5. teldeki bir notanın oktavı da iki tel ince, iki perde ileridedir (3. tel). Örnek: 5. tel 3. perde {{n:C3}} → 3. tel 5. perde {{n:C4}}.`,
        `4. ve 3. telden oktav ararken 2. telin akortu yüzünden **üç perde** ileri gidersin. Örnek: 4. tel 2. perde {{n:E3}} → 2. tel 5. perde {{n:E4}}.`,
        `6. tel ile 1. tel aynı perdede, iki oktav arayla aynı notayı verir.`,
      ]),
      compare([
        ['6. tel, 3. perde', { seq: 'G2' }],
        ['4. tel, 5. perde', { seq: 'G3' }],
      ]),
      go('6. teldeki notaları bul', 'nota.egz.sap.1'),
      go('5. teldeki notaları bul', 'nota.egz.sap.2'),
      go('{{pc:G}}–{{pc:B}} arası neden 4 yarım ses?', 'az-bilinenler.sol-si-araligi'),
    ],
  },
  {
    id: 'n5',
    title: 'Oktavlar ve frekans',
    minutes: 4,
    summary: `Notaların yüksekliğinin frekansla nasıl ölçüldüğünü ve bir oktavın neden "iki kat" demek olduğunu öğren.`,
    blocks: [
      p(
        `Ses, havanın titreşimidir. Saniyedeki titreşim sayısına **frekans** denir ve hertz (Hz) ile ölçülür. Frekans arttıkça ses incelir.`,
      ),
      list([
        `Akortun referans notası {{n:A4}}: saniyede **440** titreşim, yani 440 Hz.`,
        `Bir oktav yukarı çıkmak frekansı **ikiye katlar**: {{n:A5}} = 880 Hz. Bir oktav inmek frekansı yarıya böler: {{n:A3}} = 220 Hz.`,
        `Gitarın en kalın teli {{n:E2}} yaklaşık **82 Hz**, en ince boş teli {{n:E4}} yaklaşık **330 Hz**'dir.`,
      ]),
      compare([
        ['{{n:A2}} · 110 Hz', { seq: 'A2' }],
        ['{{n:A3}} · 220 Hz', { seq: 'A3' }],
        ['{{n:A4}} · 440 Hz', { seq: 'A4' }],
      ]),
      p(
        `Üçü de aynı notadır, yalnızca farklı yüksekliklerdedir. Kulak oktavları birbirine çok benzer duyar. Bu yüzden bir notayı farklı bir oktavda söylemek ya da çalmak çok yaygın bir hatadır.`,
      ),
      h(`Nota numaraları`),
      p(
        `Notanın yanındaki sayı, hangi oktavda olduğunu gösterir. Piyanonun ortasındaki {{pc:C}} {{n:C4}} olarak yazılır. Numara her {{pc:C}} notasında bir artar: {{n:B3}} notasından sonra {{n:C4}} gelir.`,
      ),
      tip(
        'info',
        'Gitar için yazılan notalar',
        `Gitar için yazılan notalar, okumayı kolaylaştırmak için gerçek sesten bir oktav yukarıda yazılır. Bu uygulamada ise her zaman **gerçek ses** kullanılır; örneğin 5. tel boşken {{n:A2}} çalar.`,
      ),
      table(
        ['Nota', 'Frekans', 'Gitarda'],
        [
          ['{{n:E2}}', '82,4 Hz', '6. tel boş'],
          ['{{n:A2}}', '110 Hz', '5. tel boş'],
          ['{{n:D3}}', '146,8 Hz', '4. tel boş'],
          ['{{n:G3}}', '196 Hz', '3. tel boş'],
          ['{{n:B3}}', '246,9 Hz', '2. tel boş'],
          ['{{n:E4}}', '329,6 Hz', '1. tel boş'],
          ['{{n:A4}}', '440 Hz', '1. tel, 5. perde'],
        ],
      ),
      {
        t: 'quiz',
        q: `İkinci nota birincinin kaç oktav üstünde?`,
        play: { seq: 'A2 A4' },
        options: ['Bir oktav', 'İki oktav', 'Üç oktav'],
        answer: 1,
        explain: `110 Hz'ten 440 Hz'e çıktık: frekans iki kez ikiye katlandı, yani iki oktav.`,
      },
      go('Canlı nota algılayıcıyı dene', 'nota.kesfet'),
      go('Aynı notayı üç oktavda bul', 'nota.egz.sap.6'),
      go('Perdeler neden sıklaşır?', 'az-bilinenler.perdeler-sikilasir'),
    ],
  },
  {
    id: 'n6',
    title: 'Notaları tonalitede tanımak',
    minutes: 7,
    summary: `Notaları tek başlarına değil, bir tonalitenin içindeki karakterleriyle tanımayı öğren.`,
    blocks: [
      p(
        `Mutlak kulağın yoksa, tek başına çalınan bir notanın adını bilmek çok zordur. Ama bir tonalitenin içinde her nota kendine özgü bir **karakter** kazanır. Bu bölümdeki antrenmanlar önce bir **kadansla** (I – IV – V – I akorları) tonaliteyi duyurur, sonra notayı çalar.`,
      ),
      play('{{pc:C}} majörde kadans', { cadence: 'C' }),
      h(`Derecelerin karakteri`),
      p(`Aşağıdaki notaları dinlemeden önce kadansı bir kez daha çal. Her notanın ne "istediğine" odaklan.`),
      table(
        ['Nota', 'Derece', 'Nasıl hissettirir?', 'Dinle'],
        [
          ['{{pc:C}}', '1', 'Ev. Dinlenmiş, tamamlanmış.', cell({ seq: 'C4' })],
          ['{{pc:D}}', '2', 'Hareketli; {{pc:C}} ya da {{pc:E}} notasına gitmek ister.', cell({ seq: 'D4' })],
          ['{{pc:E}}', '3', 'Parlak ve sakin; huzurlu ama evden biraz uzak.', cell({ seq: 'E4' })],
          ['{{pc:F}}', '4', 'Gergin; {{pc:E}} notasına inmek ister.', cell({ seq: 'F4' })],
          ['{{pc:G}}', '5', 'Güçlü ve sağlam; açık bir hissi vardır.', cell({ seq: 'G4' })],
          ['{{pc:A}}', '6', 'Yumuşak, biraz hüzünlü.', cell({ seq: 'A4' })],
          ['{{pc:B}}', '7', 'En gergin nota; {{pc:C}} notasına çıkmak ister.', cell({ seq: 'B4' })],
        ],
      ),
      h(`Çözülme: notayı eve götür`),
      p(
        `Bir notayı tanımanın en güvenilir yolu, onu içinden eve kadar söylemektir. Duyduğun notadan başla ve gamda adım adım {{pc:C}} notasına in ya da çık. Kaç adım attığını sayarsan notanın derecesini bulursun.`,
      ),
      compare([
        ['{{pc:B}} → {{pc:C}}', { seq: 'B4 C5' }],
        ['{{pc:F}} → {{pc:E}}', { seq: 'F4 E4' }],
        ['{{pc:D}} → {{pc:C}}', { seq: 'D4 C4' }],
        ['{{pc:A}} → {{pc:G}}', { seq: 'A4 G4' }],
      ]),
      tip(
        'info',
        'Neden işe yarıyor?',
        `Notaların karakteri tonaliteye bağlıdır. {{pc:G}} majöre geçtiğinde ev artık {{pc:G}} notasıdır ve gergin, çıkmak isteyen nota {{pc:F#}} olur. Dereceleri öğrendiğinde her tonaliteye kolayca uyum sağlarsın.`,
      ),
      {
        t: 'quiz',
        q: `Önce kadansı, sonra notayı dinle. Bu nota ne istiyor?`,
        items: [
          { label: 'Kadans', cadence: 'C' },
          { label: 'Nota', seq: 'B4:2' },
        ],
        options: [
          'Dinlenmiş, evde ({{pc:C}})',
          '{{pc:C}} notasına çıkmak istiyor ({{pc:B}})',
          'Güçlü ve sağlam ({{pc:G}})',
        ],
        answer: 1,
        explain: `Bu {{pc:B}}, yani 7. derece. Hemen ardından {{pc:C}} notasını duymak istersin.`,
      },
      {
        t: 'quiz',
        q: `Peki bu?`,
        items: [
          { label: 'Kadans', cadence: 'C' },
          { label: 'Nota', seq: 'G4:2' },
        ],
        options: ['{{pc:F}}: {{pc:E}} notasına inmek istiyor', '{{pc:G}}: güçlü ve sağlam', '{{pc:B}}: çıkmak istiyor'],
        answer: 1,
        explain: `Bu {{pc:G}}, 5. derece. Gerilim yaratmaz, ama {{pc:C}} kadar "evde" de değildir.`,
      },
      go('Hangi Nota? Seviye 1', 'nota.egz.hangi.1'),
      go('Seviye 3: yedi nota', 'nota.egz.hangi.3'),
    ],
  },
  {
    id: 'n7',
    title: 'Aralıklar',
    minutes: 8,
    summary: `İki nota arasındaki 13 temel mesafeyi adları, perde sayıları ve akılda kalıcı örneklerle öğren.`,
    blocks: [
      p(
        `İki nota arasındaki mesafeye **aralık** denir ve yarım ses sayısıyla ölçülür. Gitarda bu, aynı teldeki perde farkına eşittir. Aralıkları tanıyan bir kulak, bir melodiyi notadan notaya izleyebilir.`,
      ),
      table(
        ['Aralık', 'Yarım ses (perde)', 'Dinle', 'Hafıza ipucu'],
        [
          ["Tam 1'li (aynı nota)", '0', cell({ seq: 'C4 C4' }), '—'],
          ["Küçük 2'li", '1', cell({ seq: 'C4 C#4' }), 'Jaws teması'],
          ["Büyük 2'li", '2', cell({ seq: 'C4 D4' }), 'İyi ki doğdun (ikinci ve üçüncü nota)'],
          ["Küçük 3'lü", '3', cell({ seq: 'C4 Eb4' }), 'Smoke on the Water riffi'],
          ["Büyük 3'lü", '4', cell({ seq: 'C4 E4' }), 'When the Saints Go Marching In'],
          ["Tam 4'lü", '5', cell({ seq: 'C4 F4' }), 'La Cucaracha (adındaki "ca-ra" geçişi)'],
          ["Triton (artık 4'lü)", '6', cell({ seq: 'C4 F#4' }), 'Maria (West Side Story)'],
          ["Tam 5'li", '7', cell({ seq: 'C4 G4' }), 'Star Wars ana tema (ilk iki uzun nota)'],
          ["Küçük 6'lı", '8', cell({ seq: 'C4 Ab4' }), '—'],
          ["Büyük 6'lı", '9', cell({ seq: 'C4 A4' }), 'My Bonnie'],
          ["Küçük 7'li", '10', cell({ seq: 'C4 Bb4' }), 'Star Trek (orijinal dizi teması)'],
          ["Büyük 7'li", '11', cell({ seq: 'C4 B4' }), '—'],
          ["Oktav (tam 8'li)", '12', cell({ seq: 'C4 C5' }), 'Over the Rainbow'],
        ],
      ),
      tip(
        'info',
        'Hafıza ipuçlarını kullanmak',
        `Her ipucu, o melodinin başındaki bir geçişi işaret eder. Bildiğin şarkılardan kendi listeni yapmak daha da etkilidir: sevdiğin bir şarkının ilk notaları hangi aralıksa onu not et.`,
      ),
      h(`Gitarda aralık şekilleri`),
      list([
        `**Tam 4'lü:** bir ince telde aynı perde (3. telden 2. tele geçerken bir perde ileri).`,
        `**Tam 5'li:** bir ince telde iki perde ileri (3. telden 2. tele geçerken üç perde). Power chord'lar bu aralıktan oluşur.`,
        `**Oktav:** iki ince telde iki perde ileri (2. tele geçerken üç perde).`,
        `**Büyük 3'lü:** bir ince telde bir perde geri (3. telden 2. tele geçerken aynı perde).`,
      ]),
      compare([
        ["Yükselen 5'li", { seq: 'C4 G4' }],
        ["İnen 5'li", { seq: 'G4 C4' }],
        ['Birlikte (armonik)', { harm: 'C4 G4' }],
      ]),
      p(
        `Aralıklar yukarı da aşağı da çalınabilir; iki nota birlikte çalındığında ise armonik aralık olur. İnen aralıklar genelde daha zor tanınır, bu yüzden önce yükselenlerle başla.`,
      ),
      {
        t: 'quiz',
        q: `Bu hangi aralık?`,
        play: { seq: 'C4 G4' },
        options: ["Büyük 3'lü", "Tam 5'li", 'Oktav'],
        answer: 1,
        explain: `Bu bir tam 5'li, yani 7 yarım ses. Star Wars ana temasının ilk iki uzun notası gibi.`,
      },
      {
        t: 'quiz',
        q: `Ya bu?`,
        play: { seq: 'D4 F#4' },
        options: ["Küçük 3'lü", "Büyük 3'lü", "Tam 4'lü"],
        answer: 1,
        explain: `{{pc:D}} ile {{pc:F#}} arası 4 yarım ses: büyük 3'lü. Majör akora parlaklığını veren aralık budur.`,
      },
      go('Aralık Tanıma: Seviye 1', 'nota.egz.aralik.1'),
      go('Tüm yükselen aralıklar', 'nota.egz.aralik.4'),
    ],
  },
];
